import { Link, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ShieldCheck } from 'lucide-react';
import { SessionProvider } from './lib/session';
import { PortfolioProvider } from './lib/portfolio';
import { useSession } from './lib/session';
import { login } from './lib/api';
import { AdminShell, AppMark, Button, LoadingState, PublicShell } from './components/core';
import {
  ChangelogDetailPage,
  ChangelogPage,
  DecisionDetailPage,
  DecisionsPage,
  FeedbackDetailPage,
  FeedbackPage,
  PortalHome,
  RoadmapDetailPage,
  RoadmapPage,
} from './pages/public';
import {
  AdminAnalyticsPage,
  AdminChangelogPage,
  AdminDecisionsPage,
  AdminFeedbackPage,
  AdminOverviewPage,
  AdminRoadmapPage,
  AdminSettingsPage,
} from './pages/admin';
import { AdminPortfolioPage } from './pages/portfolio';
import { AdminTimelinePage } from './pages/timeline';

export default function App() {
  return <SessionProvider><PortfolioProvider><Routes>
    <Route element={<PublicShell />}>
      <Route index element={<PortalHome />} />
      <Route path="feedback" element={<FeedbackPage />} />
      <Route path="feedback/:postId" element={<FeedbackDetailPage />} />
      <Route path="roadmap" element={<RoadmapPage />} />
      <Route path="roadmap/:itemId" element={<RoadmapDetailPage />} />
      <Route path="changelog" element={<ChangelogPage />} />
      <Route path="changelog/:entryId" element={<ChangelogDetailPage />} />
      <Route path="decisions" element={<DecisionsPage />} />
      <Route path="decisions/:decisionId" element={<DecisionDetailPage />} />
    </Route>
    <Route path="admin" element={<AdminShell />}>
      <Route index element={<AdminOverviewPage />} />
      <Route path="feedback" element={<AdminFeedbackPage />} />
      <Route path="roadmap" element={<AdminRoadmapPage />} />
      <Route path="timeline" element={<AdminTimelinePage />} />
      <Route path="changelog" element={<AdminChangelogPage />} />
      <Route path="decisions" element={<AdminDecisionsPage />} />
      <Route path="analytics" element={<AdminAnalyticsPage />} />
      <Route path="portfolio" element={<AdminPortfolioPage />} />
      <Route path="settings" element={<AdminSettingsPage />} />
    </Route>
    <Route path="login" element={<LoginPage />} />
    <Route path="auth/login" element={<Navigate to="/login" replace />} />
    <Route path="auth/error" element={<AuthError />} />
    <Route path="404" element={<NotFound />} />
    <Route path="*" element={<Navigate to="/404" replace />} />
  </Routes></PortfolioProvider></SessionProvider>;
}

function safeLoginReturnTo(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/admin';
}

export function LoginPage() {
  const [params] = useSearchParams();
  const { user, loading, isOperator } = useSession();
  const returnTo = safeLoginReturnTo(params.get('returnTo'));
  if (loading) return <LoadingState label="Checking your session" />;
  if (user) return <Navigate to={isOperator ? returnTo : '/feedback'} replace />;
  return <main className="login-page">
    <section className="login-page__story">
      <AppMark />
      <div>
        <p className="eyebrow">Product context, connected</p>
        <h1>Sign in to keep the whole product cycle moving.</h1>
        <p>Manage feedback, roadmap commitments, release notes, and the decisions behind every change from one operational workspace.</p>
      </div>
      <ol className="login-cycle" aria-label="AppCycle workflow">
        <li><span>1</span><strong>Feedback</strong><small>Capture the signal</small></li>
        <li><span>2</span><strong>Roadmap</strong><small>Set direction</small></li>
        <li><span>3</span><strong>Changelog</strong><small>Close the loop</small></li>
        <li><span>4</span><strong>Decisions</strong><small>Preserve why</small></li>
      </ol>
    </section>
    <section className="login-card" aria-labelledby="login-title">
      <div className="authrz-mark"><ShieldCheck size={18} /><span><small>Secured by</small><strong>Authrz.me</strong></span></div>
      <div>
        <p className="eyebrow">AppCycle workspace</p>
        <h2 id="login-title">Continue with single sign-on</h2>
        <p>Authrz.me verifies your identity and supplies your AppCycle role. Passwords are never stored here.</p>
      </div>
      <Button icon={<ArrowRight size={17} />} onClick={() => login(returnTo)}>Continue with Authrz.me</Button>
      <p className="login-card__note"><ShieldCheck size={14} /> Secure OIDC with PKCE and role-based access.</p>
      <Link to="/feedback">Browse public feedback without signing in</Link>
    </section>
  </main>;
}

function AuthError() {
  const [params] = useSearchParams();
  return <main className="access-gate"><AppMark /><AlertTriangle size={34} /><h1>Sign-in didn’t complete</h1><p>Authrz.me returned <span className="mono">{params.get('code') ?? 'an unknown error'}</span>. Start a fresh sign-in flow.</p><Link className="button button--primary" to="/login">Try sign-in again</Link><Link to="/feedback">Return to public feedback</Link></main>;
}

function NotFound() {
  return <main className="access-gate"><AppMark /><span className="mono">404</span><h1>This path left the cycle</h1><p>The page does not exist or is no longer public.</p><Button onClick={() => window.location.assign('/feedback')}>Open feedback</Button></main>;
}
